import { XMLBuilder, XMLParser } from 'fast-xml-parser';

/**
 * Escape text content for editor-state XML (`&`, `<`, `>`).
 * @note at the time of writing replacements containing & is an impossible state.
 */
export function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Escape values placed inside double-quoted XML attributes. */
export function escapeXmlAttr(value: string): string {
  return escapeXml(value).replace(/"/g, '&quot;');
}

export type XmlTextNode = {
  type: 'text';
  value: string;
};

export type XmlElementNode = {
  type: 'element';
  name: string;
  attributes: Record<string, string>;
  children: XmlNode[];
};

export type XmlNode = XmlTextNode | XmlElementNode;

const DOCUMENT_WRAPPER = 'document';

const EDITOR_XML_PARSER_OPTIONS = {
  ignoreAttributes: false,
  preserveOrder: true,
  trimValues: false,
  processEntities: true,
} as const;

const EDITOR_XML_BUILDER_OPTIONS = {
  ...EDITOR_XML_PARSER_OPTIONS,
  processEntities: false,
  suppressEmptyNode: true,
} as const;

const parser = new XMLParser(EDITOR_XML_PARSER_OPTIONS);
const builder = new XMLBuilder(EDITOR_XML_BUILDER_OPTIONS);

type OrderedXmlItem = {
  '#text'?: string;
  ':@'?: Record<string, string>;
  [key: string]: unknown;
};

const parseAttributes = (attributes?: Record<string, string>) => {
  if (!attributes) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(attributes).map(([key, value]) => [
      key.replace(/^@_/, ''),
      String(value),
    ])
  );
};

const parseOrderedItem = (item: OrderedXmlItem): XmlNode[] => {
  if (item['#text'] !== undefined) {
    return [{ type: 'text', value: String(item['#text']) }];
  }

  return Object.entries(item)
    .filter(([key]) => key !== ':@')
    .flatMap(([name, value]) => {
      if (!Array.isArray(value)) {
        return [];
      }

      return [
        {
          type: 'element' as const,
          name,
          attributes: parseAttributes(item[':@']),
          children: value.flatMap(child =>
            parseOrderedItem(child as OrderedXmlItem)
          ),
        },
      ];
    });
};

const xmlNodesToOrdered = (nodes: XmlNode[]): OrderedXmlItem[] =>
  nodes.map(node => {
    if (node.type === 'text') {
      return { '#text': escapeXml(node.value) };
    }

    const children = xmlNodesToOrdered(node.children);
    const element: OrderedXmlItem = { [node.name]: children };

    if (Object.keys(node.attributes).length > 0) {
      element[':@'] = Object.fromEntries(
        Object.entries(node.attributes).map(([key, value]) => [
          `@_${key}`,
          escapeXmlAttr(value),
        ])
      );
    }

    return element;
  });

const unwrapDocument = (xml: string) =>
  xml
    .replace(new RegExp(`^<${DOCUMENT_WRAPPER}>`), '')
    .replace(new RegExp(`</${DOCUMENT_WRAPPER}>$`), '');

const OPEN_TAG_PATTERN =
  /^<([a-z][a-z0-9_-]*)((?:\s+[a-zA-Z_:-]+="[^"]*")*)\s*>/;
const CLOSE_TAG_PATTERN = /^<\/([a-z][a-z0-9_-]*)>/;

const assertValidEditorXml = (xml: string) => {
  const withoutEntities = xml.replace(/&(?:lt|gt|quot|amp);/g, () => '\u0000');

  for (let index = 0; index < withoutEntities.length; index += 1) {
    if (withoutEntities[index] === '<') {
      const remainder = withoutEntities.slice(index);
      const isValidTag =
        OPEN_TAG_PATTERN.test(remainder) || CLOSE_TAG_PATTERN.test(remainder);

      if (!isValidTag) {
        throw new Error(`Invalid XML near: ${xml.slice(index, index + 32)}`);
      }
    }
  }
};

/**
 * Parse editor-state XML into a simple node tree.
 * @param xml - The XML to parse.
 * @returns The node tree.
 * @todo consider not using xml parser in favor of a custom parser
 */
export function xmlToObject(xml: string): XmlNode[] {
  assertValidEditorXml(xml);

  const parsed = parser.parse(
    `<${DOCUMENT_WRAPPER}>${xml}</${DOCUMENT_WRAPPER}>`
  ) as Array<Record<string, OrderedXmlItem[]>>;

  const documentNodes = parsed[0]?.[DOCUMENT_WRAPPER];

  if (!documentNodes) {
    return [];
  }

  return documentNodes.flatMap(item => parseOrderedItem(item));
}

/**
 * Serialize a node tree back to editor-state XML.
 * @param nodes - The node tree to serialize.
 * @returns The serialized XML.
 * @todo consider not using xml builder in favor of a custom serializer
 */
export function objectToXml(nodes: XmlNode[]): string {
  const built = builder.build([
    { [DOCUMENT_WRAPPER]: xmlNodesToOrdered(nodes) },
  ]) as string;

  return unwrapDocument(built);
}

const xmlNodesToPlainText = (nodes: XmlNode[]): string =>
  nodes
    .map(node =>
      node.type === 'text' ? node.value : xmlNodesToPlainText(node.children)
    )
    .join('');

const XML_ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&amp;': '&',
};

/**
 * Plain text of stable editor state (`section.editor` / v2 `take.formattedText`):
 * every cue and replacement unwrapped to the text it was applied to, matching
 * what the editor reports as its plain text once hydrated.
 */
export function editorToPlainText(editor: string): string {
  try {
    return xmlNodesToPlainText(xmlToObject(editor));
  } catch {
    // Unparseable markup still has to show something - drop the tags.
    return editor
      .replace(/<\/?[a-z][^>]*>/gi, '')
      .replace(/&(?:lt|gt|quot|amp);/g, entity => XML_ENTITIES[entity]);
  }
}
